import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import auth from '../../../config/utils/auth';
import modelFieldMessages from '../../../config/utils/modelFieldMessages';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import Render3DScene from '../Render3DScene';
import { buildHyperBeamPreviewModel, findHyperBeamZeroTangentPoint } from '../Render3DScene/models/hyperBeamUtils';
import { normalizeShoePositions, parseLslFile, parseShoePositionCSV } from './CadworkUtils';
import useCurrentUser from '../../../config/utils/hooks/useCurrentUser';
import { isBelowMinPrivilege } from '../../../config/utils/permissions_roles/roles';

function DataTable({ title, columns, rows, emptyText = 'No data available', fitContent = false, usePaper = true }) {
  const tableContainerProps = usePaper ? { component: Paper, variant: 'outlined' } : { component: Box };

  return (
    <Box sx={{ flex: fitContent ? '0 0 auto' : 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: fitContent ? 'auto' : '100%' }}>
      {title ? (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          {title}
        </Typography>
      ) : null}
      <TableContainer
        {...tableContainerProps}
        sx={{
          flex: fitContent ? '0 0 auto' : 1,
          overflow: fitContent ? 'visible' : 'auto',
          ...(!usePaper ? { border: '1px solid #e0e0e0', borderRadius: 1 } : {})
        }}>
        <Table size="small" stickyHeader={!fitContent}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align || 'left'} sx={{ fontWeight: 700, py: 0.75 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, index) => (
                <TableRow key={row.id || row.index || `${title}-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                  {columns.map((column) => (
                    <TableCell key={`${column.key}-${index}`} align={column.align || 'left'} sx={{ py: 0.4, fontSize: '0.75rem' }}>
                      {row[column.key] ?? ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function resolveSequenceSource(item, sequenceFields = ['sequence']) {
  const directCandidates = [...sequenceFields.filter(Boolean).map((field) => item?.[field]), item?.lamellaSequence, item?.lamellas, item?.sequence];
  const nestedCandidates = Array.isArray(item?.layers) ? item.layers.flatMap((layer) => [layer?.lamellas, layer?.lamellaSequence, layer?.sequence]) : [];

  return [...directCandidates, ...nestedCandidates].find((entry) => Array.isArray(entry) && entry.length) || [];
}

export default function StructurewoodCadworkTab({
  orderId,
  model = 'bshorder',
  item,
  changeModelState,
  onOrderUpdated,
  showPrepareButton = true,
  prepareMinPrivilege,
  contentPadding = 0,
  sequenceFields = ['sequence'],
  referenceLabel = '1st Tightening from right',
  pressureLabel = 'Pressure (bar)',
  pressureField = 'direction',
  hideMissingLslWarning = false
}) {
  const intl = useIntl();
  const fm = (msg) => intl.formatMessage(msg);
  const { currentUser } = useCurrentUser();

  // Prepare rewrites the order's shoe/sequence data, so it is a write action.
  // Projects that don't set prepareMinPrivilege keep the previous behaviour.
  const canPrepare = showPrepareButton && !isBelowMinPrivilege({ minPrivilege: prepareMinPrivilege }, currentUser?.privilege);
  // eslint-disable-next-line no-unused-vars
  const [existingFiles, setExistingFiles] = useState({});
  const [attachedLslData, setAttachedLslData] = useState(null);
  const [attachedShoeData, setAttachedShoeData] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [clamps] = useState(item.pressure);

  const authHeaders = useMemo(() => ({ Authorization: 'Bearer ' + auth.getToken() }), []);

  const refreshExistingFiles = useCallback(async () => {
    if (!orderId || !model) {
      setExistingFiles({});
      setAttachedLslData(null);
      return;
    }

    setLoadingFiles(true);
    try {
      const checks = await Promise.all(
        ['shoePosition', 'lslFile', 'pdfDescription'].map(async (fileType) => {
          const response = await axios.post('/api/v1/files/check_file_for_model/', { model, id: orderId, fileType }, { headers: authHeaders });
          return [fileType, response.data.exists ? response.data : null];
        })
      );

      const nextFiles = Object.fromEntries(checks);
      setExistingFiles(nextFiles);

      const [lslResponse, shoeResponse] = await Promise.all([
        nextFiles.lslFile?.fileId ? axios.get(`/api/v1/files/${nextFiles.lslFile.fileId}/serve/`, { responseType: 'text', headers: authHeaders }) : Promise.resolve(null),
        nextFiles.shoePosition?.fileId ? axios.get(`/api/v1/files/${nextFiles.shoePosition.fileId}/serve/`, { responseType: 'text', headers: authHeaders }) : Promise.resolve(null)
      ]);

      setAttachedLslData(lslResponse ? parseLslFile(lslResponse.data || '') : null);
      setAttachedShoeData(shoeResponse ? parseShoePositionCSV(shoeResponse.data || '') : null);
    } catch (fetchError) {
      console.error('Failed to check Structurewood files', fetchError);
      setExistingFiles({});
      setAttachedLslData(null);
      setAttachedShoeData(null);
    } finally {
      setLoadingFiles(false);
    }
  }, [authHeaders, model, orderId]);

  useEffect(() => {
    refreshExistingFiles();
  }, [refreshExistingFiles, refreshKey]);

  const handlePrepare = useCallback(async () => {
    if (!orderId) {
      setError('Order must exist before Structurewood data can be prepared.');
      return;
    }

    if (!item?.lamellaWidth || !item?.lamellaThickness) {
      setError('Width and thickness are required before preparing Structurewood data.');
      return;
    }

    setWorking(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `/api/v1/orders/bsh/${orderId}/prepare_structurewood_data/`,
        {
          lamellaWidth: item.lamellaWidth,
          lamellaThickness: item.lamellaThickness
        },
        { headers: authHeaders }
      );

      if (onOrderUpdated) {
        onOrderUpdated(response.data);
      }

      setSuccess('Structurewood data prepared successfully.');
      setRefreshKey((value) => value + 1);
    } catch (prepareError) {
      console.error('Failed to prepare Structurewood data', prepareError);
      setError(prepareError?.response?.data?.error || 'Failed to prepare Structurewood data.');
    } finally {
      setWorking(false);
    }
  }, [authHeaders, item?.lamellaThickness, item?.lamellaWidth, onOrderUpdated, orderId]);

  const resolvedSequence = useMemo(() => resolveSequenceSource(item, sequenceFields), [item, sequenceFields]);
  const normalizedShoePositions = useMemo(() => normalizeShoePositions(item?.shoePositions, attachedShoeData?.positions), [attachedShoeData?.positions, item?.shoePositions]);
  const shoeCount = normalizedShoePositions.length || 0;

  const sequenceRows = useMemo(() => {
    if (!resolvedSequence.length) return [];
    const groups = [];
    resolvedSequence.forEach((entry) => {
      const last = groups[groups.length - 1];
      if (
        last &&
        last.length === entry.length &&
        last.quality === (entry.quality || entry.grade) &&
        last.startPosition === entry.startPosition &&
        last.endPosition === entry.endPosition &&
        last.rightGap === entry.rightGap
      ) {
        last.count += 1;
      } else {
        groups.push({
          length: entry.length,
          quality: entry.quality || entry.grade || '',
          startPosition: entry.startPosition,
          endPosition: entry.endPosition,
          rightGap: entry.rightGap,
          count: 1
        });
      }
    });
    let runningIndex = 1;
    return groups.map((group) => {
      const from = runningIndex;
      const to = runningIndex + group.count - 1;
      runningIndex = to + 1;
      return {
        index: from === to ? `${from}` : `${from}-${to}`,
        length: group.length,
        quality: group.quality,
        startPosition: group.startPosition,
        endPosition: group.endPosition,
        rightGap: group.rightGap
      };
    });
  }, [resolvedSequence]);

  const hyperBeamPayload = useMemo(
    () => ({
      shoePositions: item?.shoePositions,
      fallbackShoePositions: attachedShoeData?.positions,
      sequence: resolvedSequence,
      lamellaWidthMm: item?.lamellaWidth,
      lamellaThicknessMm: item?.lamellaThickness,
      referenceFromOriginMm: attachedLslData?.referenceFromOriginMm,
      referenceFromExtremityMm: attachedLslData?.referenceFromExtremityMm,
      shoeSpacingMm: attachedLslData?.shoeSpacingMm || 400
    }),
    [
      attachedLslData?.referenceFromExtremityMm,
      attachedLslData?.referenceFromOriginMm,
      attachedLslData?.shoeSpacingMm,
      attachedShoeData?.positions,
      item?.lamellaThickness,
      item?.lamellaWidth,
      item?.shoePositions,
      resolvedSequence
    ]
  );

  const hyperBeamPreview = useMemo(() => buildHyperBeamPreviewModel(hyperBeamPayload, { beamRenderProfile: 'lehmann' }), [hyperBeamPayload]);
  const zeroTangentAnalysis = useMemo(() => findHyperBeamZeroTangentPoint(hyperBeamPayload), [hyperBeamPayload]);
  const visibleWarnings = useMemo(
    () => (hyperBeamPreview?.warnings || []).filter((warning) => !(hideMissingLslWarning && warning === 'LSL reference data is missing; HyperBeamModel uses fallback wrap distribution.')),
    [hideMissingLslWarning, hyperBeamPreview?.warnings]
  );
  const previewBounds = hyperBeamPreview?.bounds;
  const previewSpanX = previewBounds ? (previewBounds.maxX - previewBounds.minX) * 0.0015 : 0;
  const previewCenterX = previewBounds ? (previewBounds.maxX + previewBounds.minX) * 0.0005 : 0;
  const previewCenterZ = previewBounds ? (previewBounds.maxZ + previewBounds.minZ) * 0.0005 : 0;
  const previewCameraHeight = Math.max(20, previewSpanX * 1.0);
  const previewCameraOffsetX = previewCenterX;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: contentPadding }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField label={fm(modelFieldMessages.calibreLamellaWidth)} value={item?.lamellaWidth ?? 500} size="small" sx={{ minWidth: 220 }} disabled />
        <TextField label={fm(modelFieldMessages.calibreLamellaThickness)} value={item?.lamellaThickness ?? 30} size="small" sx={{ minWidth: 220 }} disabled />
        <TextField label={referenceLabel} value={attachedLslData?.referenceFromExtremityMm ?? ''} size="small" sx={{ minWidth: 220 }} disabled />
        <TextField label={pressureLabel} value={item?.[pressureField] ?? ''} size="small" sx={{ minWidth: 220 }} disabled />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${shoeCount} ${fm(modelFieldMessages.calibreShoes)}`} size="small" />
          <Chip label={`${resolvedSequence.length || 0} ${fm(modelFieldMessages.calibreLamellas)}`} size="small" color={resolvedSequence.length ? 'success' : 'default'} />
        </Stack>
        {canPrepare ? (
          <Box sx={{ ml: 'auto' }}>
            <Button variant="contained" color="warning" disabled={working} onClick={handlePrepare}>
              {working ? <CircularProgress size={18} color="inherit" /> : fm(modelFieldMessages.calibrePrepareRecalculate)}
            </Button>
          </Box>
        ) : null}
      </Stack>

      {(error || success) && (
        <Stack spacing={1}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
        </Stack>
      )}

      {loadingFiles ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : null}

      <Stack direction="row" spacing={2} alignItems="stretch" sx={{ flexWrap: 'nowrap' }}>
        <Paper variant="outlined" sx={{ flex: 1.25, p: 1.5, minHeight: 420, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          {hyperBeamPreview?.errors?.length ? <Alert severity="error">{hyperBeamPreview.errors[0]}</Alert> : null}

          {visibleWarnings.map((warning) => (
            <Alert key={warning} severity="warning" sx={{ py: 0 }}>
              {warning}
            </Alert>
          ))}

          <Box sx={{ flex: 1, minHeight: 340, borderRadius: 1, overflow: 'hidden', border: '1px solid #e3e3e3', background: '#f4f1eb' }}>
            {hyperBeamPreview?.status === 'ready' ? (
              <Render3DScene
                type="hyperBeamModel"
                beamRenderProfile="lehmann"
                data={{
                  hyperBeam: {
                    ...hyperBeamPayload,
                    analysisMarker: zeroTangentAnalysis?.status === 'ready' ? zeroTangentAnalysis : null
                  },
                  beamOptions: { beamRenderProfile: 'lehmann' }
                }}
                canvasStyle={{ height: '100%', width: '100%', minHeight: 340, position: 'relative' }}
                cameraConfig={{
                  position: [previewCameraOffsetX, previewCameraHeight, previewCenterZ + previewCameraHeight * 0.05],
                  zoom: 1.1,
                  fov: 36,
                  background: '#f4f1eb',
                  lightSettings: {
                    ambientLight: { intensity: 0.3 },
                    directionalLights: [{ position: [-16, 9, -14], intensity: 0.34, castShadow: false }],
                    spotLight: []
                  },
                  orbitControl: {
                    enabled: true,
                    target: [previewCenterX, 0, previewCenterZ],
                    maxDistance: previewCameraHeight * 2,
                    minDistance: 5,
                    maxPolarAngle: Math.PI
                  }
                }}
                options={{ plane: false, axes: false }}
              />
            ) : (
              <Box sx={{ height: '100%', minHeight: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', px: 3, textAlign: 'center' }}>
                <Typography variant="body2">HyperBeamModel preview needs lamella width, lamella thickness, shoe positions and a prepared sequence.</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        <DataTable
          title="Lamella sequence"
          fitContent
          usePaper={false}
          columns={[
            { key: 'index', label: '#' },
            { key: 'length', label: fm(modelFieldMessages.calibreColLength) },
            { key: 'quality', label: fm(modelFieldMessages.calibreColType) },
            { key: 'startPosition', label: fm(modelFieldMessages.calibreColStart) },
            { key: 'endPosition', label: fm(modelFieldMessages.calibreColEnd) },
            { key: 'rightGap', label: fm(modelFieldMessages.calibreColGap) }
          ]}
          rows={sequenceRows}
          emptyText="No parsed sequence yet"
        />
      </Stack>
    </Box>
  );
}
